/**
 * Standard parish dropdown label:
 * ParishName (uniqueId) | city | deaneryName | parishCategoryName
 * If uniqueId is missing, the name is shown without parentheses.
 * If deaneryName is missing, that piece is omitted.
 * Use omitCategory: true for OTHER-category groups (ParishName (uniqueId) | city | deaneryName).
 */

export interface ParishLabelInput {
  id?: string | number | null;
  parish?: string | null;
  uniqueId?: string | number | null;
  city?: string | null;
  deaneryName?: string | null;
  parishCategoryName?: string | null;
  [key: string]: unknown;
}

export interface ParishLabelOptions {
  omitCategory?: boolean;
}

export function formatParishOptionLabel(
  item: ParishLabelInput | null | undefined,
  options: ParishLabelOptions = {},
): string {
  if (!item) return "";
  const { omitCategory = false } = options;
  const name = item.parish || "";
  const uidRaw = item.uniqueId;
  const uid =
    uidRaw != null && String(uidRaw).trim() !== ""
      ? String(uidRaw).trim()
      : null;
  const nameWithId = uid ? `${name} (${uid})` : name;

  const city = item.city;
  const deanery = item.deaneryName;
  const cat = omitCategory ? null : item.parishCategoryName;

  const extras = [city, deanery, cat].filter(Boolean);
  if (extras.length === 0) {
    return nameWithId || "No details available";
  }
  return `${nameWithId} | ${extras.join(" | ")}`;
}

export interface ParishSelectOption {
  label: string;
  value: string | number | null;
}

export interface ParishOptionGroup {
  label: string;
  options: ParishSelectOption[];
}

export function sortParishesByName<T extends { parish?: string | null }>(
  data: T[] | null | undefined,
): T[] {
  if (!data) return [];
  return [...data].sort((a, b) =>
    (a?.parish ?? "").localeCompare(b?.parish ?? ""),
  );
}

export function buildGroupedParishOptions(
  data: ParishLabelInput[] | null | undefined,
  options: { includeNoneOther?: boolean } = {},
): ParishOptionGroup[] {
  const { includeNoneOther = false } = options;
  const normalParishes: ParishSelectOption[] = [];
  const otherParishes: ParishSelectOption[] = [];

  data?.forEach((item) => {
    if (!item.parish) return;
    if (item.parishCategoryName?.toUpperCase() === "OTHER") {
      otherParishes.push({
        label: formatParishOptionLabel(item, { omitCategory: true }),
        value: item.id as string | number,
      });
    } else {
      normalParishes.push({
        label: formatParishOptionLabel(item),
        value: item.id as string | number,
      });
    }
  });

  normalParishes.sort((a, b) => a.label.localeCompare(b.label));
  otherParishes.sort((a, b) => a.label.localeCompare(b.label));

  const groups: ParishOptionGroup[] = [];
  if (includeNoneOther) {
    groups.push({
      label: "",
      options: [
        { label: "NONE / NINGUNA", value: null },
        { label: "OTHER / OTRA", value: "OTHER" },
      ],
    });
  }
  groups.push(
    { label: "Select Parish", options: normalParishes },
    { label: "OTHER", options: otherParishes },
  );
  return groups;
}
